import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { Flag } from '@/components/Flag';
import type { IdealListRow } from '@/helpers/analytics/computeIdealList';
import { applySwapToList } from '@/helpers/scoring/matchdayScoring';
import { countriesInfo, type Country } from '@/models/country';
import type { PublicIdealListRow } from '@/models/publicStats';

export type ListScoreBreakdownRow = IdealListRow | PublicIdealListRow;

/** Min container width to fit rank + flag + full team name + arithmetic on one line. */
const SHOW_TEAM_NAMES_MIN_WIDTH_PX = 520;

function hasSegmentFields(row: ListScoreBreakdownRow): boolean {
  return typeof row.pointsBefore === 'number' && typeof row.pointsAfter === 'number';
}

/** Team sits on a crossed rank (pre- and post-swap multipliers differ). */
function isCrossedRow(row: ListScoreBreakdownRow): boolean {
  return (row.multiplierAfter ?? row.multiplier) !== row.multiplier;
}

/** Display rows in post-swap rank order; arithmetic on each row is unchanged. */
function orderRowsByPostSwap(
  rows: ListScoreBreakdownRow[],
  countryList: Country[],
  swapPair: [number, number] | null,
): ListScoreBreakdownRow[] {
  if (rows.length !== 10 || countryList.length !== 10) return rows;
  const byCountry = new Map(rows.map((r) => [r.country, r]));
  const ordered: ListScoreBreakdownRow[] = [];
  for (const country of applySwapToList(countryList, swapPair)) {
    const row = byCountry.get(country);
    if (row) ordered.push(row);
  }
  return ordered.length === 10 ? ordered : rows;
}

function Op(props: { children: string }) {
  return (
    <Text as='span' color='gray.500' textAlign='center' aria-hidden>
      {props.children}
    </Text>
  );
}

/**
 * Faithful score breakdown.
 * Crossed ranks: pointsBefore × m_before + pointsAfter × m_after.
 * Unmoved ranks: rawPoints × multiplier (same m before and after).
 * Shared [= contrib] columns keep totals aligned.
 */
function Arithmetic(props: {
  row: ListScoreBreakdownRow;
  /** Reserve width for two-term formulas when any row needs them. */
  wideFormula: boolean;
  compact?: boolean;
}) {
  const { row, wideFormula, compact } = props;
  const mAfter = row.multiplierAfter ?? row.multiplier;
  const pre = row.pointsBefore ?? row.rawPoints;
  const post = row.pointsAfter ?? 0;
  const crossed = hasSegmentFields(row) && isCrossedRow(row);
  const fontSize = compact ? 'xs' : 'sm';
  const numSx = { fontVariantNumeric: 'tabular-nums' } as const;

  // Wide enough for "47 × 10 + 17 × 10" with tabular nums; single-term right-aligns inside.
  const formulaMinW = wideFormula ? (compact ? '11.5rem' : '13.5rem') : undefined;

  return (
    <HStack
      spacing={compact ? 1 : 1.5}
      flexShrink={0}
      ml='auto'
      fontSize={fontSize}
      sx={numSx}
      align='center'
    >
      <Box
        minW={formulaMinW}
        display='flex'
        justifyContent='flex-end'
        alignItems='center'
        gap={compact ? 1 : 1.5}
      >
        {crossed ? (
          <>
            <Text as='span'>{pre}</Text>
            <Op>×</Op>
            <Text as='span'>{row.multiplier}</Text>
            <Op>+</Op>
            <Text as='span'>{post}</Text>
            <Op>×</Op>
            <Text as='span'>{mAfter}</Text>
          </>
        ) : (
          <>
            <Text as='span'>{row.rawPoints}</Text>
            <Op>×</Op>
            <Text as='span'>{row.multiplier}</Text>
          </>
        )}
      </Box>
      <Op>=</Op>
      <Text fontWeight='semibold' minW='3.25ch' textAlign='right'>
        {row.contribution}
      </Text>
    </HStack>
  );
}

export type ListScoreBreakdownProps = {
  score: number;
  swapPair: [number, number] | null;
  /** Base (pre-swap) country list — used for swap labels and post-swap row order. */
  countryList: Country[];
  rows: ListScoreBreakdownRow[];
  /** When false, show ×multiplier only (legacy public doc without rows). */
  showArithmetic?: boolean;
  /** Label before swap ranks, e.g. "Ideal swap" or "Swap". */
  swapLabel?: string;
  compact?: boolean;
};

/** Score + swap header and per-team contribution rows (Stats optimal list / My Points Arithmetic). */
export function ListScoreBreakdown(props: ListScoreBreakdownProps) {
  const {
    score,
    swapPair,
    countryList,
    rows,
    showArithmetic = true,
    swapLabel = 'Swap',
    compact = false,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [showTeamNames, setShowTeamNames] = useState(true);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const update = (width: number) => {
      setShowTeamNames(width >= SHOW_TEAM_NAMES_MIN_WIDTH_PX);
    };

    update(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) update(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const displayRows = useMemo(
    () => orderRowsByPostSwap(rows, countryList, swapPair),
    [rows, countryList, swapPair],
  );

  const wideFormula =
    showArithmetic && displayRows.some((r) => hasSegmentFields(r) && isCrossedRow(r));

  return (
    <Box ref={containerRef}>
      <HStack mb={4} spacing={4} flexWrap='wrap' justify='center' textAlign='center'>
        <Text fontSize='sm'>
          Score: <strong>{score}</strong>
        </Text>
        {swapPair ? (
          <Text fontSize='sm'>
            {swapLabel}: ranks <strong>{swapPair[0] + 1}</strong> ↔ <strong>{swapPair[1] + 1}</strong>{' '}
            ({countriesInfo[countryList[swapPair[0]]]?.name} ↔{' '}
            {countriesInfo[countryList[swapPair[1]]]?.name})
          </Text>
        ) : (
          <Text fontSize='sm' color='gray.500'>
            No swap applied
          </Text>
        )}
      </HStack>

      <VStack align='stretch' spacing={0}>
        {displayRows.map((row, i) => {
          const teamName = countriesInfo[row.country]?.name ?? row.country;
          return (
            <Box
              key={`${row.country}-${i}`}
              py={2}
              px={1}
              borderTopWidth={i === 0 ? 0 : '1px'}
              borderColor='blackAlpha.100'
              _dark={{ borderColor: 'whiteAlpha.200' }}
            >
              <HStack spacing={compact ? 2 : 3} align='center' w='full'>
                <Text fontSize='sm' fontWeight='bold' w='28px' flexShrink={0} textAlign='left'>
                  {i + 1}
                </Text>
                <Flag country={row.country} w={6} borderRadius='3px' svg flexShrink={0} />
                {showTeamNames ? (
                  <Text
                    fontSize='sm'
                    fontWeight='medium'
                    flex={1}
                    minW={0}
                    textAlign='left'
                    whiteSpace='nowrap'
                  >
                    {teamName}
                  </Text>
                ) : null}
                {showArithmetic ? (
                  <Arithmetic row={row} wideFormula={wideFormula} compact={compact} />
                ) : (
                  <Text fontSize='xs' color='gray.500' flexShrink={0} ml='auto'>
                    ×{row.multiplierAfter ?? row.multiplier}
                  </Text>
                )}
              </HStack>
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
}
